import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { UiActions } from 'modules/ui';
import { AccountActions } from 'modules/account';
import { NO_SELECT_NUMBER } from 'constants/form';
import apiInnovator from 'external/api/innovator';
import { RegisterProfileFormCareerSecondValues } from 'types/profile';
import RequestError from 'classes/RequestError';
import { UpdateInnovatorRequest } from 'proto/v1/innovatorservice/innovatorservice';
import {
  MAX_PREFERRED_WORK_LOCATIONS,
  RESUME_FILE_DEFAULT,
} from 'constants/profile';
import { range } from 'utils/array';
import { scrollLayoutBodyToTop } from 'utils/layout';
import {
  getDefaultCareerSkillsValue,
  getDefaultPreferredWorkLocationJapanPrefectureIdsValue,
  getDefaultPreferredOccupationIdsValue,
  getDefaultCareerExpectationsValue,
  getDefaultOutputsValue,
  getDefaultCertificationsValue,
} from 'utils/profile/defaultFormValues';
import {
  getCareerSkillIdsValueToUpdate,
  getPreferredWorkLocationsValueToUpdate,
  getPreferredOccupationIdsValueToUpdate,
  getCareerExpectationIdsValueToUpdate,
  getOutputsValueToUpdate,
  getCertificationsValueToUpdate,
} from 'utils/profile/formValuesToUpdate';
import {
  careerSkillsSchema,
  englishSkillIdSchema,
  outputsSchema,
  preferredWorkLocationJapanPrefectureIdSchema,
  preferredOccupationsSchema,
  careerPreferredEmploymentTypeIdSchema,
  careerPreferredMonthlyUnitPriceIdSchema,
  careerExpectationsSchema,
  selfIntroductionSchema,
} from 'utils/profile/schema';
import { Stepper } from 'hooks/useStepper';
import useErrorNotification from 'hooks/useErrorNotification';
import useCareerSkillsFormField from 'hooks/profile/useCareerSkillsFormField';
import useCareerExpectationsFormField from 'hooks/profile/useCareerExpectationsFormField';
import useResumeFileFormField from 'hooks/profile/useCareerResumeFileFormField';
import usePreferredOccupationsFormField from 'hooks/profile/usePreferredOccupationsFormField';
import useFetchInnovatorForm from 'hooks/profile/useFetchInnovatorForm';
import { stepsCareer } from '../../steps';

const schema = object().shape({
  englishSkillId: englishSkillIdSchema,
  preferredWorkLocationJapanPrefectureIds: preferredWorkLocationJapanPrefectureIdSchema,
  preferredOccupations: preferredOccupationsSchema,
  outputs: outputsSchema,
  career: object().shape({
    skills: careerSkillsSchema,
    preferredEmploymentTypeId: careerPreferredEmploymentTypeIdSchema,
    preferredMonthlyUnitPriceId: careerPreferredMonthlyUnitPriceIdSchema,
    expectations: careerExpectationsSchema,
  }),
  selfIntroduction: selfIntroductionSchema,
});

const defaultValues: RegisterProfileFormCareerSecondValues = {
  englishSkillId: NO_SELECT_NUMBER,
  preferredWorkLocationJapanPrefectureIds: range(
    0,
    MAX_PREFERRED_WORK_LOCATIONS - 1,
  ).map(() => NO_SELECT_NUMBER),
  preferredOccupations: [],
  outputs: [],
  career: {
    skills: [],
    expectations: [],
    preferredEmploymentTypeId: NO_SELECT_NUMBER,
    achievement: '',
    preferredMonthlyUnitPriceId: NO_SELECT_NUMBER,
    resumeFile: {
      name: '',
      size: 0,
      key: '',
      url: '',
    },
  },
  certifications: [],
  selfIntroduction: '',
};

const useRegisterProfileFormCareerSecond = ({
  stepper,
}: {
  stepper: Stepper;
}) => {
  const dispatch = useDispatch();
  const { handleRequestError } = useErrorNotification();
  const { formItems, innovator, innovatorToUpdate } = useFetchInnovatorForm();

  const [isValidDefault, setIsValidDefault] = useState<boolean>(false);

  const formMethods = useForm<RegisterProfileFormCareerSecondValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset } = formMethods;

  const {
    skillsOptionsState,
    exampleSkills,
    handleClickExampleSkill,
    handleInputChangeSkill,
    handleCreateSkillOption,
    fetchMoreSkillOptions,
  } = useCareerSkillsFormField({ formMethods });

  const {
    expectationsOptionsState,
    exampleExpectations,
    handleClickExampleExpectation,
    handleInputChangeExpectation,
    handleCreateExpectationOption,
    fetchMoreExpectationOptions,
  } = useCareerExpectationsFormField({ formMethods });

  const { preferredOccupationsError } = usePreferredOccupationsFormField({
    formMethods,
  });

  const {
    resumeFile,
    setResumeFile,
    handleDropResumeFile,
    handleRemoveResumeFile,
  } = useResumeFileFormField({
    formMethods,
  });

  const handleClickPrevButton = useCallback(() => {
    scrollLayoutBodyToTop();
    stepper.handlePrev();
  }, [stepper]);

  const onSubmit = useCallback(
    async (values: RegisterProfileFormCareerSecondValues) => {
      const valuesToUpdate: UpdateInnovatorRequest = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...innovatorToUpdate!,
        englishSkillId: values.englishSkillId,
        preferredWorkLocations: getPreferredWorkLocationsValueToUpdate(
          values.preferredWorkLocationJapanPrefectureIds,
        ),
        preferredOccupationIds: getPreferredOccupationIdsValueToUpdate(
          values.preferredOccupations,
        ),
        certifications: getCertificationsValueToUpdate(values.certifications),
        selfIntroduction: values.selfIntroduction,
        outputs: getOutputsValueToUpdate(values.outputs),
        career: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...innovatorToUpdate!.career!,
          achievement: values.career.achievement,
          skillIds: getCareerSkillIdsValueToUpdate(values.career.skills),
          expectationIds: getCareerExpectationIdsValueToUpdate(
            values.career.expectations,
          ),
          preferredEmploymentTypeId: values.career.preferredEmploymentTypeId,
          fileKey: values.career.resumeFile.key,
          preferredMonthlyUnitPriceId:
            values.career.preferredMonthlyUnitPriceId,
        },
      };
      let data;
      try {
        ({ data } = await apiInnovator.updateInnovator(valuesToUpdate));
      } catch (error: unknown) {
        if (error instanceof RequestError) {
          dispatch(UiActions.setLoading(false));
          handleRequestError(error);
        }
        return;
      }
      dispatch(AccountActions.setInnovator(data.innovator));
      dispatch(UiActions.setLoading(false));
      scrollLayoutBodyToTop();
      stepsCareer.formSecond.completed = true;
      stepper.handleNext();
    },
    [dispatch, handleRequestError, innovatorToUpdate, stepper],
  );

  // Set default values by the fetched innovator
  useEffect(() => {
    if (!innovatorToUpdate || !innovator) return;

    const values: RegisterProfileFormCareerSecondValues = {
      englishSkillId: innovatorToUpdate.englishSkillId,
      preferredWorkLocationJapanPrefectureIds: getDefaultPreferredWorkLocationJapanPrefectureIdsValue(
        innovatorToUpdate.preferredWorkLocations,
      ),
      preferredOccupations: getDefaultPreferredOccupationIdsValue(
        innovatorToUpdate.preferredOccupationIds,
      ),
      selfIntroduction: innovatorToUpdate.selfIntroduction,
      certifications: getDefaultCertificationsValue(
        innovatorToUpdate.certifications,
      ),
      outputs: getDefaultOutputsValue(innovatorToUpdate.outputs),
      career: {
        skills: getDefaultCareerSkillsValue(innovator.career?.skills),
        expectations: getDefaultCareerExpectationsValue(
          innovator.career?.expectations,
        ),
        preferredEmploymentTypeId:
          innovatorToUpdate.career?.preferredEmploymentTypeId ??
          NO_SELECT_NUMBER,
        achievement: innovatorToUpdate.career?.achievement ?? '',
        resumeFile: innovator.career?.resumeFile ?? RESUME_FILE_DEFAULT,
        preferredMonthlyUnitPriceId:
          innovatorToUpdate.career?.preferredMonthlyUnitPriceId ??
          NO_SELECT_NUMBER,
      },
    };

    setResumeFile(innovator.career?.resumeFile ?? RESUME_FILE_DEFAULT);

    setIsValidDefault(schema.isValidSync(values));

    reset(values);
  }, [formItems.countries, innovator, innovatorToUpdate, reset, setResumeFile]);

  return {
    innovator,
    onSubmit,
    formMethods,
    formItems,
    exampleSkills,
    skillsOptionsState,
    expectationsOptionsState,
    exampleExpectations,
    resumeFile,
    preferredOccupationsError,
    isValidDefault,
    handleClickPrevButton,
    fetchMoreSkillOptions,
    handleInputChangeSkill,
    handleCreateSkillOption,
    handleClickExampleSkill,
    fetchMoreExpectationOptions,
    handleClickExampleExpectation,
    handleInputChangeExpectation,
    handleCreateExpectationOption,
    handleDropResumeFile,
    handleRemoveResumeFile,
  };
};

export default useRegisterProfileFormCareerSecond;
